import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, Building2, Loader2, ArrowLeft, Mail, UserPlus, Trash2, ShieldCheck, User as UserIcon, Download } from "lucide-react";
import { LOGO_PIN } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "geodyn_token";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

export default function Admin() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);

  const loadData = useCallback(async (user) => {
    try {
      const l = await axios.get(`${API}/leads`, authHeaders());
      setLeads(l.data);
      if (user.role === "admin") {
        const u = await axios.get(`${API}/auth/users`, authHeaders());
        setUsers(u.data);
      }
    } catch (e) {
      toast.error("Couldn't load data", { description: "Please log in again." });
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, authHeaders())
      .then((r) => { setMe(r.data); return loadData(r.data); })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, [loadData]);

  const onLogin = async (user, token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setMe(user);
    await loadData(user);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setMe(null);
    setLeads([]);
    setUsers([]);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
      </div>
    );
  }

  if (!me) return <LoginScreen onLogin={onLogin} />;

  return <Dashboard me={me} leads={leads} setLeads={setLeads} users={users} setUsers={setUsers} logout={logout} />;
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      await onLogin(data.user, data.token);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Login failed", { description: typeof d === "string" ? d : "Invalid email or password." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 grain relative">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-surface/70 backdrop-blur-xl p-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_PIN} alt="GeoDyn" className="h-16 w-16 object-contain drop-shadow-[0_0_12px_rgba(0,229,255,0.5)]" />
          <div>
            <div className="font-heading font-extrabold text-lg">GEODYN <span className="text-brand-green">CONSOLE</span></div>
            <div className="font-mono text-[11px] tracking-widest uppercase text-white/50">Sign in</div>
          </div>
        </div>

        <label className="mt-8 block font-mono text-xs tracking-wider uppercase text-white/60">Email</label>
        <div className="mt-2 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@geodynparking.com"
            className="w-full rounded-md bg-ink border border-white/15 pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none" />
        </div>

        <label className="mt-5 block font-mono text-xs tracking-wider uppercase text-white/60">Password</label>
        <div className="mt-2 relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            className="w-full rounded-md bg-ink border border-white/15 pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none" />
        </div>

        <button data-testid="admin-login-btn" type="submit" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green py-3 font-semibold text-black hover:bg-[#9fd94f] transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
        <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"><ArrowLeft className="h-3 w-3" /> Back to site</a>
      </form>
    </div>
  );
}

function Dashboard({ me, leads, setLeads, users, setUsers, logout }) {
  const orgs = new Set(leads.map((l) => l.organization)).size;

  const replyHref = (l) =>
    `mailto:${l.email}?subject=${encodeURIComponent("Re: Your GeoDyn Parking demo request")}` +
    `&body=${encodeURIComponent(`Hi ${l.name},\n\nThank you for your interest in GeoDyn Parking, the intelligence layer for parking. I'd love to set up a quick demo to walk you through how we can help ${l.organization || "your team"}.\n\nAre you available for a short call this week?\n\nBest regards,\n${me.name}\nGeoDyn Parking`)}`;

  const exportCSV = () => {
    const headers = ["Name", "Email", "Organization", "Role", "Message", "Received"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
    const rows = leads.map((l) => [l.name, l.email, l.organization, l.role, l.message, new Date(l.created_at).toLocaleString()]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `geodyn-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported", { description: `${leads.length} demo request(s).` });
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, authHeaders());
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Request deleted");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Couldn't delete request", { description: typeof d === "string" ? d : "Try again." });
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface/40 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_PIN} alt="GeoDyn" className="h-14 w-14 object-contain drop-shadow-[0_0_12px_rgba(0,229,255,0.5)]" />
            <span className="font-heading font-extrabold">GEODYN <span className="text-brand-green">CONSOLE</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-2 text-sm text-white/60">
              {me.role === "admin" ? <ShieldCheck className="h-4 w-4 text-brand-green" /> : <UserIcon className="h-4 w-4 text-brand-cyan" />}
              {me.name} · <span className="font-mono text-xs uppercase">{me.role}</span>
            </span>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 space-y-12">
        <section>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">Demo Requests</h1>
            <button
              data-testid="export-csv-btn"
              onClick={exportCSV}
              disabled={leads.length === 0}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 hover:border-brand-green/50 transition-colors disabled:opacity-40"
            >
              <Download className="h-4 w-4 text-brand-green" /> Export CSV
            </button>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
            <StatCard icon={Users} label="Total Leads" value={leads.length} color="text-brand-green" />
            <StatCard icon={Building2} label="Organizations" value={orgs} color="text-brand-cyan" />
          </div>

          <div className="mt-8 rounded-2xl border border-white/10 overflow-hidden" data-testid="admin-leads-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface/60 text-left font-mono text-xs uppercase tracking-wider text-white/50">
                  <tr>
                    <th className="px-5 py-4">Name</th><th className="px-5 py-4">Email</th><th className="px-5 py-4">Organization</th>
                    <th className="px-5 py-4">Role</th><th className="px-5 py-4">Message</th><th className="px-5 py-4">Received</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-white/40">No demo requests yet.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} data-testid="admin-lead-row" className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-4 font-medium">{l.name}</td>
                      <td className="px-5 py-4 text-brand-cyan">{l.email}</td>
                      <td className="px-5 py-4">{l.organization}</td>
                      <td className="px-5 py-4 text-white/60">{l.role || "—"}</td>
                      <td className="px-5 py-4 text-white/60 max-w-xs truncate">{l.message || "—"}</td>
                      <td className="px-5 py-4 font-mono text-xs text-white/40">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-3">
                          <a href={replyHref(l)} data-testid="reply-lead-btn" title={`Reply to ${l.email}`} className="inline-flex items-center gap-1.5 text-xs text-brand-cyan hover:text-white transition-colors">
                            <Mail className="h-3.5 w-3.5" /> Reply
                          </a>
                          {me.role === "admin" && (
                            <button data-testid="delete-lead-btn" onClick={() => deleteLead(l.id)} title="Delete request" className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors">
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {me.role === "admin" && <TeamSection me={me} users={users} setUsers={setUsers} />}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-surface/60 p-5">
      <div className="flex items-center gap-2 text-white/50"><Icon className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">{label}</span></div>
      <div className={`mt-2 font-heading font-extrabold text-3xl ${color}`}>{value}</div>
    </div>
  );
}

function TeamSection({ me, users, setUsers }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/users`, form, authHeaders());
      setUsers((prev) => [...prev, data]);
      setForm({ name: "", email: "", password: "", role: "user" });
      toast.success("User created", { description: `${data.email} can now sign in.` });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Couldn't create user", { description: typeof d === "string" ? d : "Please check the details." });
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (id, email) => {
    try {
      await axios.delete(`${API}/auth/users/${id}`, authHeaders());
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("User removed", { description: email });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Couldn't remove user", { description: typeof d === "string" ? d : "Try again." });
    }
  };

  return (
    <section data-testid="admin-team-section">
      <h2 className="font-heading font-extrabold text-3xl tracking-tight">Team & Access</h2>
      <p className="mt-2 text-white/60">Create logins for teammates. Admins manage users; users can view demo requests.</p>

      <form onSubmit={addUser} data-testid="create-user-form" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 rounded-2xl border border-white/10 bg-surface/60 p-6">
        <input data-testid="user-name-input" required value={form.name} onChange={set("name")} placeholder="Full name"
          className="rounded-md bg-ink border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none" />
        <input data-testid="user-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="Email"
          className="rounded-md bg-ink border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none" />
        <input data-testid="user-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Temp password"
          className="rounded-md bg-ink border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none" />
        <select data-testid="user-role-select" value={form.role} onChange={set("role")}
          className="rounded-md bg-ink border border-white/15 px-4 py-3 text-white focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none">
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <button data-testid="create-user-btn" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-green px-4 py-3 font-semibold text-black hover:bg-[#9fd94f] transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add
        </button>
      </form>

      <div className="mt-6 rounded-2xl border border-white/10 overflow-hidden" data-testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface/60 text-left font-mono text-xs uppercase tracking-wider text-white/50">
              <tr><th className="px-5 py-4">Name</th><th className="px-5 py-4">Email</th><th className="px-5 py-4">Role</th><th className="px-5 py-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid="admin-user-row" className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-4 font-medium">{u.name}</td>
                  <td className="px-5 py-4 text-brand-cyan">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-mono uppercase ${u.role === "admin" ? "bg-brand-green/15 text-brand-green" : "bg-brand-cyan/15 text-brand-cyan"}`}>
                      {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}{u.role}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {u.id === me.id ? (
                      <span className="text-xs text-white/30">You</span>
                    ) : (
                      <button data-testid="delete-user-btn" onClick={() => removeUser(u.id, u.email)} className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

